(() => {
  'use strict';

  const SELECTOR = '[data-ugb-root]';

  const safeJson = (node, fallback = null) => {
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); } catch (error) {
      console.error('[Ultimate Garage] Invalid JSON', error, node);
      return fallback;
    }
  };

  const list = (value) => String(value || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  const clamp = (number, min, max) => Math.min(max, Math.max(min, number));

  const esc = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  class UltimateGarageBuilder {
    constructor(root) {
      if (!root || root.dataset.ugbInitialized === 'true') return;
      root.dataset.ugbInitialized = 'true';

      this.root = root;
      this.config = safeJson(root.querySelector('[data-ugb-config]'), {});
      this.products = [...root.querySelectorAll('script[data-ugb-product]')].map((node) => safeJson(node)).filter(Boolean);
      this.mounts = [...root.querySelectorAll('script[data-ugb-mount]')].map((node) => safeJson(node)).filter(Boolean);
      this.productMap = new Map(this.products.map((product) => [String(product.key).toLowerCase(), product]));
      this.mountMap = new Map(this.mounts.map((mount) => [String(mount.key).toLowerCase(), mount]));
      this.categoryMap = new Map((this.config.categories || []).map((category) => [category.id, category]));
      this.audienceMap = new Map((this.config.audiences || []).map((audience) => [audience.id, audience]));

      this.elements = {
        content: root.querySelector('[data-ugb-content]'),
        nav: root.querySelector('[data-ugb-nav]'),
        back: root.querySelector('[data-ugb-back]'),
        next: root.querySelector('[data-ugb-next]'),
        wall: root.querySelector('[data-ugb-wall]'),
        objects: root.querySelector('[data-ugb-objects]'),
        wallEmpty: root.querySelector('[data-ugb-wall-empty]'),
        bgImage: root.querySelector('[data-ugb-background-image]'),
        brick: root.querySelector('[data-ugb-brick]'),
        bgButtons: root.querySelector('[data-ugb-background-buttons]'),
        bgUpload: root.querySelector('[data-ugb-background-upload]'),
        bgZoom: root.querySelector('[data-ugb-bg-zoom]'),
        bgX: root.querySelector('[data-ugb-bg-x]'),
        bgY: root.querySelector('[data-ugb-bg-y]'),
        bgReset: root.querySelector('[data-ugb-bg-reset]'),
        savings: root.querySelector('[data-ugb-savings]'),
        save: root.querySelector('[data-ugb-save]'),
        share: root.querySelector('[data-ugb-share]'),
        reset: root.querySelector('[data-ugb-reset]'),
        toast: root.querySelector('[data-ugb-toast]'),
        stepLabel: root.querySelector('[data-ugb-step-label]'),
        progressLabel: root.querySelector('[data-ugb-progress-label]'),
        progressBar: root.querySelector('[data-ugb-progress-bar]')
      };

      this.instanceCounter = 0;
      this.state = this.freshState();
      this.bind();
      const restoredFromUrl = this.restoreFromUrl();
      if (!restoredFromUrl) this.restoreFromLocal();
      this.render();
    }

    freshState() {
      return {
        guided: null,
        audience: null,
        currentStep: 'audience',
        instances: [],
        background: {
          kind: 'preset',
          key: 'default',
          url: this.backgroundByKey('default')?.url || '',
          zoom: 1,
          x: 0,
          y: 0
        }
      };
    }

    bind() {
      this.elements.content?.addEventListener('click', (event) => this.onContentClick(event));
      this.elements.back?.addEventListener('click', () => this.goBack());
      this.elements.next?.addEventListener('click', () => this.goNext());
      this.elements.objects?.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.elements.save?.addEventListener('click', () => this.saveSetup());
      this.elements.share?.addEventListener('click', () => this.shareSetup());
      this.elements.reset?.addEventListener('click', () => this.resetSetup());
      this.elements.bgButtons?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bg-key]');
        if (button) this.setBackgroundPreset(button.dataset.bgKey);
      });
      this.elements.bgUpload?.addEventListener('change', (event) => this.uploadBackground(event));
      this.elements.bgZoom?.addEventListener('input', () => this.updateBackgroundAdjustments());
      this.elements.bgX?.addEventListener('input', () => this.updateBackgroundAdjustments());
      this.elements.bgY?.addEventListener('input', () => this.updateBackgroundAdjustments());
      this.elements.bgReset?.addEventListener('click', () => this.resetBackgroundAdjustments());
    }

    backgroundByKey(key) {
      return (this.config.backgrounds || []).find((background) => background.key === key);
    }

    selectedKeys() {
      return new Set(this.state.instances.map((instance) => String(instance.productKey).toLowerCase()));
    }

    conditionsPass(item) {
      const selected = this.selectedKeys();
      const any = list(item.showIfAnySelected);
      const all = list(item.showIfAllSelected);
      const hide = list(item.hideIfAnySelected);
      if (any.length && !any.some((key) => selected.has(key))) return false;
      if (all.length && !all.every((key) => selected.has(key))) return false;
      if (hide.length && hide.some((key) => selected.has(key))) return false;
      return true;
    }

    productEligible(product) {
      if (!product || !this.conditionsPass(product)) return false;
      const only = list(product.onlyAudiences);
      if (this.state.guided && this.state.audience && only.length && !only.includes(this.state.audience)) return false;
      return true;
    }

    mountEligible(mount) {
      return !!mount && this.conditionsPass(mount);
    }

    categoryProducts(categoryId) {
      return this.products.filter((product) => product.category === categoryId && this.productEligible(product));
    }

    categoryInstances(categoryId) {
      return this.state.instances.filter((instance) => this.productMap.get(instance.productKey)?.category === categoryId);
    }

    compatibleMounts(instance) {
      const product = this.productMap.get(instance.productKey);
      if (!product?.mountGroup) return [];
      const group = String(product.mountGroup).trim().toLowerCase();
      return this.mounts.filter((mount) => String(mount.compatibleGroup || '').trim().toLowerCase() === group && this.mountEligible(mount));
    }

    isRecommended(item) {
      if (!this.state.guided || !this.state.audience) return false;
      return list(item.recommendedAudiences).includes(this.state.audience);
    }

    journey() {
      const journey = [{ id: 'audience', type: 'audience', label: 'Your setup' }];
      for (const category of this.config.categories || []) {
        if (!category.enabled) continue;
        const visibleProducts = this.categoryProducts(category.id);
        const selected = this.categoryInstances(category.id);
        if (!visibleProducts.length && !selected.length) continue;
        journey.push({ id: `category:${category.id}`, type: 'category', categoryId: category.id, label: category.heading });
        if (selected.some((instance) => this.compatibleMounts(instance).length > 0)) {
          journey.push({ id: `mount:${category.id}`, type: 'mount', categoryId: category.id, label: 'Mounting' });
        }
      }
      journey.push({ id: 'summary', type: 'summary', label: 'Your garage' });
      return journey;
    }

    step() {
      const journey = this.journey();
      return journey.find((step) => step.id === this.state.currentStep) || journey[0];
    }

    render() {
      this.renderBackgroundButtons();
      this.renderBackground();
      this.renderWall();
      this.renderSavings();

      if (!this.products.length) {
        this.renderProductSetupNotice();
        if (this.elements.nav) this.elements.nav.hidden = true;
        if (this.elements.stepLabel) this.elements.stepLabel.textContent = this.config.designMode ? 'Add products' : 'Garage Builder';
        if (this.elements.progressLabel) this.elements.progressLabel.textContent = this.config.designMode ? 'Quick setup' : '';
        if (this.elements.progressBar) this.elements.progressBar.style.width = '0%';
        return;
      }

      const step = this.step();
      if (this.state.currentStep !== step.id) this.state.currentStep = step.id;
      this.renderProgress();
      this.renderStep(step);
      this.renderNav(step);
    }

    renderProductSetupNotice() {
      if (!this.elements.content) return;
      if (!this.config.designMode) {
        this.elements.content.innerHTML = `
          <div class="ugb__setup-notice">
            <p class="ugb__kicker">ULTIMATE GARAGE</p>
            <h2>We're getting the garage ready.</h2>
            <p class="ugb__lead">Products haven't been configured for this builder yet.</p>
          </div>
        `;
        return;
      }

      this.elements.content.innerHTML = `
        <div class="ugb__setup-notice">
          <p class="ugb__kicker">QUICK START</p>
          <h2>Add your first real Shopify products.</h2>
          <p class="ugb__lead">The visual builder is ready. It just needs products connected to it.</p>
          <div class="ugb__setup-callout">
            <strong>In the Theme Editor sidebar:</strong>
            <ol>
              <li>Open this <strong>Ultimate Garage Builder</strong> section.</li>
              <li>Click <strong>Add block → Builder product</strong>, or use the starter blocks included with V1.3.</li>
              <li>Choose the real Shopify product.</li>
              <li>Set its category, for example <strong>WashR</strong> or <strong>BlowR</strong>.</li>
              <li>Upload the transparent <strong>Floor PNG</strong>. If you leave it blank, the Shopify product image is used for testing.</li>
              <li>If it can be mounted, give it a <strong>Mount compatibility group</strong>, then add a matching <strong>Mount option</strong> block.</li>
            </ol>
          </div>
          <p class="ugb__lead">As soon as the first product is selected, this setup screen disappears and the guided customer journey becomes live.</p>
        </div>
      `;
    }

    renderProgress() {
      const journey = this.journey();
      const index = Math.max(0, journey.findIndex((step) => step.id === this.state.currentStep));
      const humanIndex = index + 1;
      const percent = journey.length <= 1 ? 0 : (index / (journey.length - 1)) * 100;
      if (this.elements.stepLabel) this.elements.stepLabel.textContent = journey[index]?.label || '';
      if (this.elements.progressLabel) this.elements.progressLabel.textContent = `Step ${humanIndex} of ${journey.length}`;
      if (this.elements.progressBar) this.elements.progressBar.style.width = `${percent}%`;
    }

    renderStep(step) {
      if (!this.elements.content) return;
      if (step.type === 'audience') return this.renderAudience();
      if (step.type === 'category') return this.renderCategory(step.categoryId);
      if (step.type === 'mount') return this.renderMount(step.categoryId);
      return this.renderSummary();
    }

    renderAudience() {
      const audiences = (this.config.audiences || []).map((audience) => `
        <button type="button" class="ugb__audience-card${this.state.audience === audience.id ? ' is-selected' : ''}" data-audience="${esc(audience.id)}">
          <span class="ugb__audience-title">${esc(audience.label)}</span>
          <span class="ugb__audience-copy">${esc(audience.description)}</span>
          <span class="ugb__audience-arrow">Choose this setup →</span>
        </button>
      `).join('');

      this.elements.content.innerHTML = `
        <div class="ugb__opening">
          <p class="ugb__kicker">START HERE</p>
          <h2>${esc(this.config.heading || "Let's build your perfect setup.")}</h2>
          <p class="ugb__lead">${esc(this.config.intro || '')}</p>
          <div class="ugb__audience-grid">${audiences}</div>
          <button type="button" class="ugb__skip" data-skip-guidance>${esc(this.config.skipLabel || 'Build it yourself →')}</button>
        </div>
      `;
    }

    sortedProducts(categoryId) {
      return this.categoryProducts(categoryId).sort((a, b) => {
        const recDiff = Number(this.isRecommended(b)) - Number(this.isRecommended(a));
        if (recDiff) return recDiff;
        const priorityDiff = (Number(b.recommendationPriority) || 0) - (Number(a.recommendationPriority) || 0);
        if (priorityDiff) return priorityDiff;
        return String(a.title).localeCompare(String(b.title));
      });
    }

    renderCategory(categoryId) {
      const category = this.categoryMap.get(categoryId);
      const products = this.sortedProducts(categoryId);
      const cards = products.map((product) => this.productCard(product, category)).join('');
      const required = category?.required ? '<span class="ugb__required-pill">Choose at least one</span>' : '<span class="ugb__optional-pill">Optional step</span>';

      this.elements.content.innerHTML = `
        <div class="ugb__step-copy">
          <div class="ugb__step-title-row"><h2>${esc(category?.heading || categoryId)}</h2>${required}</div>
          <p class="ugb__lead">${esc(category?.intro || '')}</p>
        </div>
        <div class="ugb__product-grid">${cards || '<div class="ugb__empty-state">No products are configured for this step yet.</div>'}</div>
      `;
    }

    productCard(product, category) {
      const count = this.state.instances.filter((instance) => instance.productKey === String(product.key).toLowerCase()).length;
      const quantityEnabled = Boolean(category?.quantity && product.allowQuantity);
      const recommended = this.isRecommended(product);
      const badge = recommended
        ? '<span class="ugb__badge ugb__badge--recommended">Recommended for you</span>'
        : product.guidanceType === 'upgrade'
          ? '<span class="ugb__badge">Upgrade</span>'
          : product.guidanceType === 'optional'
            ? '<span class="ugb__badge">Optional</span>'
            : '';
      const soldOut = !product.available;
      const image = product.floorImage ? `<img src="${esc(product.floorImage)}" alt="" loading="lazy">` : '<div class="ugb__image-placeholder">BIGBOI</div>';
      const compare = product.compareAtPrice > product.price ? `<s>${this.money(product.compareAtPrice)}</s>` : '';
      const detailText = product.tooltip || product.recommendationReason;
      const why = recommended && product.recommendationReason
        ? `<details class="ugb__info"><summary>Why we recommend this</summary><div>${esc(product.recommendationReason)}</div></details>`
        : '';
      const extra = detailText
        ? `<details class="ugb__info"><summary>More information</summary><div>${esc(detailText)}</div></details>`
        : '';

      let controls = '';
      if (soldOut) {
        controls = '<button class="ugb__product-action" type="button" disabled>Sold out</button>';
      } else if (quantityEnabled) {
        controls = `
          <div class="ugb__quantity" aria-label="Quantity for ${esc(product.title)}">
            <button type="button" data-product-minus="${esc(product.key)}" ${count <= 0 ? 'disabled' : ''} aria-label="Remove one">−</button>
            <span>${count}</span>
            <button type="button" data-product-plus="${esc(product.key)}" ${count >= Number(product.maxQuantity || 1) ? 'disabled' : ''} aria-label="Add one">+</button>
          </div>
        `;
      } else {
        controls = count > 0
          ? `<button class="ugb__product-action is-added" type="button" data-product-remove="${esc(product.key)}">Added ✓ &nbsp; Remove</button>`
          : `<button class="ugb__product-action" type="button" data-product-add="${esc(product.key)}">Add to my garage</button>`;
      }

      return `
        <article class="ugb__product-card${recommended ? ' is-recommended' : ''}${count ? ' is-selected' : ''}">
          <div class="ugb__product-image">${badge}${image}</div>
          <div class="ugb__product-body">
            <h3>${esc(product.title)}</h3>
            ${product.shortDescription ? `<p>${esc(product.shortDescription)}</p>` : ''}
            <div class="ugb__price">${compare}<strong>${this.money(product.price)}</strong></div>
            ${why}${extra}
            <div class="ugb__product-footer">
              ${controls}
              <a href="${esc(product.url)}" target="_blank" rel="noopener">View full product</a>
            </div>
          </div>
        </article>
      `;
    }

    renderMount(categoryId) {
      const instances = this.categoryInstances(categoryId).filter((instance) => this.compatibleMounts(instance).length > 0);
      const counts = new Map();
      const blocks = instances.map((instance) => {
        const product = this.productMap.get(instance.productKey);
        const nextCount = (counts.get(instance.productKey) || 0) + 1;
        counts.set(instance.productKey, nextCount);
        const totalSame = this.categoryInstances(categoryId).filter((item) => item.productKey === instance.productKey).length;
        const instanceLabel = totalSame > 1 ? `${product.title} #${nextCount}` : product.title;
        const options = this.compatibleMounts(instance)
          .sort((a, b) => Number(this.isRecommended(b)) - Number(this.isRecommended(a)))
          .map((mount) => this.mountCard(instance, mount))
          .join('');
        return `
          <section class="ugb__mount-instance">
            <div class="ugb__mount-instance-title">
              <span>Mounting</span>
              <h3>${esc(instanceLabel)}</h3>
            </div>
            <div class="ugb__mount-grid">
              <button type="button" class="ugb__mount-card${instance.mountKey === 'none' ? ' is-selected' : ''}" data-instance-mount="${esc(instance.id)}" data-mount-key="none">
                <span class="ugb__mount-no-icon">↘</span>
                <strong>No wall mount</strong>
                <span>Keep this item on the floor.</span>
                <em>Included option</em>
              </button>
              ${options}
            </div>
          </section>
        `;
      }).join('');

      this.elements.content.innerHTML = `
        <div class="ugb__step-copy">
          <h2>${esc(this.config.mountHeading || 'How do you want to install it?')}</h2>
          <p class="ugb__lead">${esc(this.config.mountIntro || '')}</p>
        </div>
        ${blocks}
      `;
    }

    mountCard(instance, mount) {
      const selected = instance.mountKey === String(mount.key).toLowerCase();
      const recommended = this.isRecommended(mount);
      const image = mount.thumbnail ? `<img src="${esc(mount.thumbnail)}" alt="" loading="lazy">` : '<div class="ugb__image-placeholder">MOUNT</div>';
      return `
        <button type="button" class="ugb__mount-card${selected ? ' is-selected' : ''}${recommended ? ' is-recommended' : ''}" data-instance-mount="${esc(instance.id)}" data-mount-key="${esc(mount.key)}" ${mount.available ? '' : 'disabled'}>
          ${recommended ? '<span class="ugb__badge ugb__badge--recommended">Recommended</span>' : ''}
          <span class="ugb__mount-thumb">${image}</span>
          <strong>${esc(mount.title)}</strong>
          ${mount.shortDescription ? `<span>${esc(mount.shortDescription)}</span>` : ''}
          <em>${mount.available ? this.money(mount.price) : 'Sold out'}</em>
        </button>
      `;
    }

    renderSummary() {
      const groups = this.summaryLines();
      const lines = groups.map((line) => `
        <div class="ugb__summary-line">
          <div>
            <strong>${esc(line.title)}</strong>
            <span>${line.quantity} × ${this.money(line.price)}</span>
          </div>
          <strong>${this.money(line.price * line.quantity)}</strong>
        </div>
      `).join('');
      const total = this.totalCents();
      const audience = this.state.guided && this.state.audience ? this.audienceMap.get(this.state.audience)?.label : 'Build My Own';

      this.elements.content.innerHTML = `
        <div class="ugb__summary">
          <p class="ugb__kicker">YOUR FINISHED SETUP</p>
          <h2>This is your Ultimate Garage.</h2>
          <p class="ugb__lead">Setup profile: <strong>${esc(audience || 'Build My Own')}</strong>. You can still go back and change anything.</p>
          <div class="ugb__summary-lines">${lines || '<div class="ugb__empty-state">Your garage is empty.</div>'}</div>
          <div class="ugb__summary-total"><span>Garage total</span><strong>${this.money(total)}</strong></div>
          <button type="button" class="ugb__checkout-button" data-add-garage-to-cart ${total <= 0 ? 'disabled' : ''}>${esc(this.config.cartButtonLabel || 'Add My Ultimate Garage to Cart')}</button>
          <p class="ugb__summary-note">Real Shopify products will be added to your cart using the quantities selected above.</p>
        </div>
      `;
    }

    renderNav(step) {
      if (!this.elements.nav) return;
      const isAudience = step.type === 'audience';
      const isSummary = step.type === 'summary';
      this.elements.nav.hidden = isAudience || isSummary;
      if (!isAudience && !isSummary) {
        this.elements.back.disabled = this.journey().findIndex((item) => item.id === step.id) <= 1;
        this.elements.next.textContent = 'Continue';
      }
    }

    renderBackgroundButtons() {
      if (!this.elements.bgButtons) return;
      const buttons = (this.config.backgrounds || [])
        .filter((background) => background.url || background.key === 'default')
        .map((background) => `
          <button type="button" class="ugb__background-button${this.state.background.kind === 'preset' && this.state.background.key === background.key ? ' is-active' : ''}" data-bg-key="${esc(background.key)}">${esc(background.label || background.key)}</button>
        `).join('');
      this.elements.bgButtons.innerHTML = buttons;
    }

    renderBackground() {
      const bg = this.state.background;
      const hasImage = Boolean(bg.url);
      if (this.elements.bgImage) {
        this.elements.bgImage.hidden = !hasImage;
        if (hasImage && this.elements.bgImage.src !== bg.url) this.elements.bgImage.src = bg.url;
        this.elements.bgImage.style.transform = `translate(${bg.x}%, ${bg.y}%) scale(${bg.zoom})`;
      }
      if (this.elements.brick) this.elements.brick.hidden = hasImage;
      if (this.elements.bgZoom) this.elements.bgZoom.value = String(Math.round(bg.zoom * 100));
      if (this.elements.bgX) this.elements.bgX.value = String(bg.x);
      if (this.elements.bgY) this.elements.bgY.value = String(bg.y);
    }

    renderWall() {
      if (!this.elements.objects) return;
      this.elements.wallEmpty.hidden = this.state.instances.length > 0;
      this.elements.objects.innerHTML = this.state.instances.map((instance) => {
        const product = this.productMap.get(instance.productKey);
        if (!product) return '';
        const mount = instance.mountKey && instance.mountKey !== 'none' ? this.mountMap.get(instance.mountKey) : null;
        const image = mount?.mountedImage || product.floorImage;
        const width = Number(mount?.mountedWidth || product.visualWidth || 24);
        const mounted = Boolean(mount);
        return `
          <button
            type="button"
            class="ugb__wall-object${mounted ? ' is-mounted' : ' is-floor'}"
            data-instance-id="${esc(instance.id)}"
            style="left:${instance.x}%;top:${instance.y}%;width:${width}%;z-index:${100 + Math.round(Number(instance.y) || 0)}"
            aria-label="Move ${esc(product.title)}"
            title="Drag to reposition ${esc(product.title)}"
          >
            ${image ? `<img src="${esc(image)}" alt="${esc(product.title)}" draggable="false">` : `<span class="ugb__object-fallback">${esc(product.title)}</span>`}
          </button>
        `;
      }).join('');
    }

    renderSavings() {
      if (!this.elements.savings) return;
      const discounts = this.config.discounts || {};
      if (!discounts.enabled) {
        this.elements.savings.hidden = true;
        return;
      }
      this.elements.savings.hidden = false;
      const total = this.totalCents();
      const tiers = (discounts.tiers || [])
        .filter((tier) => Number(tier.threshold) > 0 && Number(tier.percent) > 0)
        .sort((a, b) => Number(a.threshold) - Number(b.threshold));
      const unlocked = tiers.filter((tier) => total >= Number(tier.threshold)).at(-1) || null;
      const next = tiers.find((tier) => total < Number(tier.threshold)) || null;
      const currentPercent = Number(unlocked?.percent || 0);
      const estimatedSaving = Math.round(total * currentPercent / 100);
      let progress = 100;
      if (next) {
        const previousThreshold = Number(unlocked?.threshold || 0);
        const range = Math.max(1, Number(next.threshold) - previousThreshold);
        progress = clamp(((total - previousThreshold) / range) * 100, 0, 100);
      }

      this.elements.savings.innerHTML = `
        <div class="ugb__savings-top">
          <div><span>YOUR GARAGE</span><strong>${this.money(total)}</strong></div>
          <div class="ugb__savings-tier"><span>${currentPercent ? `${currentPercent}% UNLOCKED` : 'BUILD YOUR SAVING'}</span>${estimatedSaving ? `<strong>Est. ${this.money(estimatedSaving)} saved</strong>` : ''}</div>
        </div>
        <div class="ugb__discount-track"><span style="width:${progress}%"></span></div>
        <div class="ugb__discount-message">${next ? `Add <strong>${this.money(Number(next.threshold) - total)}</strong> more to unlock <strong>${next.percent}%</strong>.` : tiers.length ? '<strong>Top discount tier unlocked ✓</strong>' : 'Configure discount tiers in the section settings.'}</div>
      `;
    }

    onContentClick(event) {
      const audience = event.target.closest('[data-audience]');
      if (audience) {
        this.state.guided = true;
        this.state.audience = audience.dataset.audience;
        this.state.currentStep = this.firstCategoryStep();
        this.render();
        return;
      }

      if (event.target.closest('[data-skip-guidance]')) {
        this.state.guided = false;
        this.state.audience = null;
        this.state.currentStep = this.firstCategoryStep();
        this.render();
        return;
      }

      const add = event.target.closest('[data-product-add]');
      if (add) return this.addProduct(add.dataset.productAdd);
      const plus = event.target.closest('[data-product-plus]');
      if (plus) return this.addProduct(plus.dataset.productPlus);
      const remove = event.target.closest('[data-product-remove]');
      if (remove) return this.removeProduct(remove.dataset.productRemove);
      const minus = event.target.closest('[data-product-minus]');
      if (minus) return this.removeProduct(minus.dataset.productMinus);

      const mount = event.target.closest('[data-instance-mount]');
      if (mount) return this.chooseMount(mount.dataset.instanceMount, mount.dataset.mountKey);

      if (event.target.closest('[data-add-garage-to-cart]')) this.addGarageToCart();
    }

    firstCategoryStep() {
      return this.journey().find((step) => step.type === 'category')?.id || 'summary';
    }

    addProduct(key) {
      const normalized = String(key).toLowerCase();
      const product = this.productMap.get(normalized);
      if (!product || !product.available || !this.productEligible(product)) return;
      const category = this.categoryMap.get(product.category);
      const current = this.state.instances.filter((instance) => instance.productKey === normalized).length;
      const qtyEnabled = Boolean(category?.quantity && product.allowQuantity);
      if (!qtyEnabled && current >= 1) return;
      if (current >= Number(product.maxQuantity || 1)) return;

      const jitter = current * 3;
      this.instanceCounter += 1;
      this.state.instances.push({
        id: `${normalized}-${Date.now().toString(36)}-${this.instanceCounter}`,
        productKey: normalized,
        mountKey: null,
        x: clamp(Number(product.floorX || 50) + jitter, 4, 96),
        y: clamp(Number(product.floorY || 83) - Math.min(jitter, 8), 4, 96)
      });
      this.render();
    }

    removeProduct(key) {
      const normalized = String(key).toLowerCase();
      const index = [...this.state.instances].map((instance) => instance.productKey).lastIndexOf(normalized);
      if (index >= 0) this.state.instances.splice(index, 1);
      this.ensureCurrentStepExists();
      this.render();
    }

    chooseMount(instanceId, mountKey) {
      const instance = this.state.instances.find((item) => item.id === instanceId);
      if (!instance) return;
      const product = this.productMap.get(instance.productKey);
      if (!product) return;

      if (mountKey === 'none') {
        instance.mountKey = 'none';
        instance.x = Number(product.floorX || 50);
        instance.y = Number(product.floorY || 83);
      } else {
        const normalized = String(mountKey).toLowerCase();
        const mount = this.mountMap.get(normalized);
        if (!mount || !mount.available || !this.compatibleMounts(instance).some((option) => String(option.key).toLowerCase() === normalized)) return;
        instance.mountKey = normalized;
        instance.x = Number(mount.wallX || product.wallX || 50);
        instance.y = Number(mount.wallY || product.wallY || 42);
      }
      this.render();
    }

    goNext() {
      const step = this.step();
      if (step.type === 'category') {
        const category = this.categoryMap.get(step.categoryId);
        if (category?.required && this.categoryInstances(step.categoryId).length === 0) {
          this.toast(`Choose at least one ${category.heading || 'product'} to continue.`);
          return;
        }
      }
      if (step.type === 'mount') {
        const unresolved = this.categoryInstances(step.categoryId)
          .filter((instance) => this.compatibleMounts(instance).length > 0)
          .some((instance) => instance.mountKey === null);
        if (unresolved) {
          this.toast('Choose a mount or “No wall mount” for each item to continue.');
          return;
        }
      }
      const journey = this.journey();
      const index = journey.findIndex((item) => item.id === step.id);
      this.state.currentStep = journey[Math.min(index + 1, journey.length - 1)]?.id || 'summary';
      this.render();
      this.scrollPanelIntoView();
    }

    goBack() {
      const journey = this.journey();
      const index = journey.findIndex((item) => item.id === this.state.currentStep);
      if (index <= 1) return;
      this.state.currentStep = journey[index - 1]?.id || 'audience';
      this.render();
      this.scrollPanelIntoView();
    }

    ensureCurrentStepExists() {
      if (!this.journey().some((step) => step.id === this.state.currentStep)) {
        this.state.currentStep = this.firstCategoryStep();
      }
    }

    scrollPanelIntoView() {
      if (window.matchMedia('(max-width: 899px)').matches) {
        this.root.querySelector('.ugb__panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    startDrag(event) {
      const object = event.target.closest('[data-instance-id]');
      if (!object || !this.elements.wall) return;
      const instance = this.state.instances.find((item) => item.id === object.dataset.instanceId);
      if (!instance) return;
      event.preventDefault();
      object.setPointerCapture?.(event.pointerId);
      object.classList.add('is-dragging');

      const move = (moveEvent) => {
        const rect = this.elements.wall.getBoundingClientRect();
        instance.x = clamp(((moveEvent.clientX - rect.left) / rect.width) * 100, 2, 98);
        instance.y = clamp(((moveEvent.clientY - rect.top) / rect.height) * 100, 2, 98);
        object.style.left = `${instance.x}%`;
        object.style.top = `${instance.y}%`;
        object.style.zIndex = String(100 + Math.round(instance.y));
      };
      const end = () => {
        object.classList.remove('is-dragging');
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end, { once: true });
      window.addEventListener('pointercancel', end, { once: true });
    }

    setBackgroundPreset(key) {
      const background = this.backgroundByKey(key);
      if (!background) return;
      this.state.background = { kind: 'preset', key, url: background.url || '', zoom: 1, x: 0, y: 0 };
      this.renderBackgroundButtons();
      this.renderBackground();
    }

    uploadBackground(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.toast('Please choose an image file.');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        this.toast('That image is over 15 MB. Please use a smaller garage photo.');
        event.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.state.background = { kind: 'upload', key: 'upload', url: String(reader.result), zoom: 1, x: 0, y: 0 };
        this.renderBackgroundButtons();
        this.renderBackground();
        this.toast('Garage photo loaded. Use Adjust background to line it up.');
      };
      reader.readAsDataURL(file);
    }

    updateBackgroundAdjustments() {
      this.state.background.zoom = Number(this.elements.bgZoom?.value || 100) / 100;
      this.state.background.x = Number(this.elements.bgX?.value || 0);
      this.state.background.y = Number(this.elements.bgY?.value || 0);
      this.renderBackground();
    }

    resetBackgroundAdjustments() {
      this.state.background.zoom = 1;
      this.state.background.x = 0;
      this.state.background.y = 0;
      this.renderBackground();
    }

    totalCents() {
      return this.state.instances.reduce((total, instance) => {
        const product = this.productMap.get(instance.productKey);
        const mount = instance.mountKey && instance.mountKey !== 'none' ? this.mountMap.get(instance.mountKey) : null;
        return total + Number(product?.price || 0) + Number(mount?.price || 0);
      }, 0);
    }

    summaryLines() {
      const lines = new Map();
      const add = (key, title, price) => {
        const existing = lines.get(key) || { key, title, price: Number(price || 0), quantity: 0 };
        existing.quantity += 1;
        lines.set(key, existing);
      };
      for (const instance of this.state.instances) {
        const product = this.productMap.get(instance.productKey);
        if (product) add(`p:${product.variantId}`, product.title, product.price);
        if (instance.mountKey && instance.mountKey !== 'none') {
          const mount = this.mountMap.get(instance.mountKey);
          if (mount) add(`m:${mount.variantId}`, mount.title, mount.price);
        }
      }
      return [...lines.values()];
    }

    cartLines() {
      const map = new Map();
      const add = (variantId) => {
        if (!variantId) return;
        const key = String(variantId);
        map.set(key, (map.get(key) || 0) + 1);
      };
      for (const instance of this.state.instances) {
        add(this.productMap.get(instance.productKey)?.variantId);
        if (instance.mountKey && instance.mountKey !== 'none') add(this.mountMap.get(instance.mountKey)?.variantId);
      }
      return [...map.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
    }

    async addGarageToCart() {
      const items = this.cartLines();
      if (!items.length) {
        this.toast('Add some gear to your garage first.');
        return;
      }
      const button = this.elements.content.querySelector('[data-add-garage-to-cart]');
      if (button) { button.disabled = true; button.textContent = 'Adding your garage…'; }
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add the garage to cart.');

        this.root.dispatchEvent(new CustomEvent('ultimate-garage:added-to-cart', { bubbles: true, detail: { items, response: data } }));
        if (this.config.cartBehavior === 'stay') {
          this.toast('Your Ultimate Garage has been added to cart.');
          if (button) { button.disabled = false; button.textContent = this.config.cartButtonLabel; }
        } else {
          window.location.assign(`${root}cart`);
        }
      } catch (error) {
        console.error('[Ultimate Garage] Cart error', error);
        this.toast(error.message || 'Something went wrong while adding to cart.');
        if (button) { button.disabled = false; button.textContent = this.config.cartButtonLabel; }
      }
    }

    serializableState() {
      return {
        v: 1,
        guided: this.state.guided,
        audience: this.state.audience,
        instances: this.state.instances.map(({ id, productKey, mountKey, x, y }) => ({ id, productKey, mountKey, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 })),
        background: {
          kind: this.state.background.kind === 'upload' ? 'preset' : this.state.background.kind,
          key: this.state.background.kind === 'upload' ? 'default' : this.state.background.key,
          zoom: this.state.background.zoom,
          x: this.state.background.x,
          y: this.state.background.y
        }
      };
    }

    saveSetup() {
      try {
        localStorage.setItem(this.config.storageKey || 'bigboi-ultimate-garage-v1', JSON.stringify(this.serializableState()));
        if (this.state.background.kind === 'upload') {
          this.toast('Setup saved. Your uploaded garage photo is temporary; products and positions are saved.');
        } else {
          this.toast('Garage setup saved on this device.');
        }
      } catch (error) {
        this.toast('This browser could not save the setup.');
      }
    }

    async shareSetup() {
      const payload = this.encodeState(this.serializableState());
      const url = new URL(window.location.href);
      url.searchParams.set('ugb', payload);
      try {
        await navigator.clipboard.writeText(url.toString());
        this.toast(this.state.background.kind === 'upload'
          ? 'Share link copied. Your uploaded photo stays private on this device; the product setup will still share.'
          : 'Share link copied to clipboard.');
      } catch (error) {
        window.prompt('Copy your Ultimate Garage link:', url.toString());
      }
    }

    restoreFromUrl() {
      const url = new URL(window.location.href);
      const encoded = url.searchParams.get('ugb');
      if (!encoded) return false;
      try {
        const data = this.decodeState(encoded);
        this.applySavedState(data);
        return true;
      } catch (error) {
        console.warn('[Ultimate Garage] Could not restore shared setup', error);
        return false;
      }
    }

    restoreFromLocal() {
      try {
        const raw = localStorage.getItem(this.config.storageKey || 'bigboi-ultimate-garage-v1');
        if (!raw) return false;
        this.applySavedState(JSON.parse(raw));
        return true;
      } catch (error) {
        console.warn('[Ultimate Garage] Could not restore saved setup', error);
        return false;
      }
    }

    applySavedState(data) {
      if (!data || data.v !== 1) return;
      this.state.guided = data.guided;
      this.state.audience = this.audienceMap.has(data.audience) ? data.audience : null;
      this.state.instances = (data.instances || [])
        .filter((instance) => this.productMap.has(String(instance.productKey).toLowerCase()))
        .map((instance) => ({
          id: instance.id || `restored-${++this.instanceCounter}`,
          productKey: String(instance.productKey).toLowerCase(),
          mountKey: instance.mountKey === 'none' || this.mountMap.has(String(instance.mountKey).toLowerCase()) ? String(instance.mountKey).toLowerCase() : null,
          x: clamp(Number(instance.x || 50), 2, 98),
          y: clamp(Number(instance.y || 83), 2, 98)
        }));
      const key = data.background?.key || 'default';
      const bg = this.backgroundByKey(key) || this.backgroundByKey('default');
      this.state.background = {
        kind: 'preset', key: bg?.key || 'default', url: bg?.url || '',
        zoom: clamp(Number(data.background?.zoom || 1), 1, 2.2),
        x: clamp(Number(data.background?.x || 0), -50, 50),
        y: clamp(Number(data.background?.y || 0), -50, 50)
      };
      this.state.currentStep = this.state.guided === null ? 'audience' : 'summary';
    }

    resetSetup() {
      if (!window.confirm('Start your Ultimate Garage again? Your current selections will be cleared.')) return;
      try { localStorage.removeItem(this.config.storageKey || 'bigboi-ultimate-garage-v1'); } catch (error) {}
      const url = new URL(window.location.href);
      url.searchParams.delete('ugb');
      window.history.replaceState({}, '', url.toString());
      this.state = this.freshState();
      this.render();
    }

    encodeState(data) {
      const bytes = new TextEncoder().encode(JSON.stringify(data));
      let binary = '';
      bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
      return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
    }

    decodeState(encoded) {
      const padded = encoded.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - encoded.length % 4) % 4);
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    }

    money(cents) {
      const currency = this.config.currency || 'AUD';
      const locale = this.config.locale || undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    toast(message) {
      if (!this.elements.toast) return;
      this.elements.toast.textContent = message;
      this.elements.toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.elements.toast.hidden = true; }, 4200);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => new UltimateGarageBuilder(root));
    if (scope.matches?.(SELECTOR)) new UltimateGarageBuilder(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
